import { Auth, Config, RedeemGift } from "@cleeng/mediastore-sdk";
import axios from "axios";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logout from "../../../components/Logout";

import { API_URL, NODE_ENV, PUBLISHER_ID } from "../../../constants";
import { axiosConfig, setExpiringItem, getExpiringItem, removeExpiringItem } from "../../../utils";

function RedeemGiftPage() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(search);
  const offerId = query.get("offerId");
  const giftCode = query.get("giftCode");
  // Detect if user is coming from the app via ?fromApp=true and persist in localStorage
  const fromAppParam = query.get("fromApp");

  React.useEffect(() => {
    if (fromAppParam === "true") {
      setExpiringItem("fromApp", true); // 2 hours default
    } else if (fromAppParam === "false") {
      removeExpiringItem("fromApp");
    }
  }, [fromAppParam]);
  const fromApp = getExpiringItem("fromApp") === true;

  useEffect(() => {
    // Configure Cleeng SDK when component mounts
    Config.setPublisher(PUBLISHER_ID);
    Config.setEnvironment(NODE_ENV);
  }, []);

  const handleSuccess = () => {
    navigate(`/my-account`);
  };
  
  const host = window.location.origin;
  
  useEffect(() => {
    // Check if user is logged in
    if (!Auth.isLogged()) {
      localStorage.setItem('save_redirect_url', `/redeem-gift${search}`);
      navigate("/");
      return;
    }
    
    // Set up Cleeng configuration
    Config.setMyAccountUrl(`${host}/my-account`); // required for legal notes
    Config.setCheckoutPayPalUrls({
      // PayPal redirection URLs, required for PayPal payment
      successUrl: `${host}/my-account`,
      cancelUrl: `${host}/choose-plan`,
      errorUrl: `${host}/choose-plan`,
    });
    
    // Set JWT token from localStorage
    const authToken = localStorage.getItem("CLEENG_AUTH_TOKEN");
    if (authToken && authToken !== 'null' && authToken !== 'undefined') {
      try {
        Config.setJWT(authToken);
        console.log("JWT token set successfully");
      } catch (error) {
        console.error("Error setting JWT token:", error);
        localStorage.removeItem("CLEENG_AUTH_TOKEN");
        navigate("/");
        return;
      }
    } else {
      console.error("No valid auth token found in localStorage");
      navigate("/");
      return;
    }
  }, [host, navigate, search]);

  // Optional: Add effect to handle missing required parameters
  useEffect(() => {
    if (!giftCode) {
      console.warn("No gift code provided in URL parameters");
      // You might want to show an error message or redirect
      // For now, we'll let the RedeemGift component handle it
    }
  }, [giftCode]);

  const getOffers = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/offers?status=active&pageLimit=3`,
        axiosConfig(),
      );

      const listPlans = (res && res.data && res.data.responseData) || [];
      return listPlans;
    } catch (error) {
      console.error("Error fetching offers:", error);
    }
  };

  const isElementLoaded = async (selector, size = 1) => {
    while (document.querySelectorAll(selector).length < size) {
      await new Promise(resolve => requestAnimationFrame(resolve))
    }
    return document.querySelectorAll(selector);
  };

  const handleLoadImages = React.useCallback(async () => {
    if (!offerId) return;
    
    const plansPromise = getOffers();
    const titleElements = isElementLoaded('.msd__subscription-card__icon')
    const [plans, selectors] = await Promise.all([plansPromise, titleElements])

    for (let index = 0; index < selectors.length; index++) {
      const selector = selectors[index];
      const offer = plans.find(o => offerId.includes(o.id))
      if (offer) {
        selector.style = `background-image: url("${offer.imageUrl}");background-size: cover;color: transparent;`
      }
    }
  }, [offerId]);

  useEffect(() => {
    if (offerId) {
      handleLoadImages()
    }
  }, [offerId, handleLoadImages]);

  // Set page title and meta description
  useEffect(() => {
    document.title = "Redeem Gift - Super Simple";
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute("content", "Redeem your Super Simple App gift subscription");
    }
  }, []);

  return (
    <>
      <Logout />
      {fromApp ? (
        <div className="from-app-text">
          <p>
            You've navigated to this page from a mobile application. If you wish to subscribe, please do so through that app store. To manage a subscription that was purchased through the web application, see <a href="/my-account?fromApp=true">account management</a>.
          </p>
        </div>
      ) : (
        <RedeemGift 
          onSuccess={handleSuccess}
          offerId={offerId}
          giftCode={giftCode}
        />
      )}
    </>
  );
}

export default RedeemGiftPage;